import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingList } from "@/components/common/LoadingList";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime, fmtMinutes, fmtMoney } from "@/lib/format";
import { useIsDesktop } from "@/lib/useMediaQuery";

const basisInfo = (o) => {
  const b = o.latest_basis;
  if (!b) return <span className="text-xs text-muted-foreground">Ikkje starta</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <StatusBadge kind="basis" status={b.status} />
      {b.blocking_issues?.length ? <span className="rounded-md bg-tint-destructive px-1.5 py-0.5 text-[11px] font-medium text-destructive">{b.blocking_issues.length} blokkering{b.blocking_issues.length > 1 ? "ar" : ""}</span> : null}
      <span className="num text-xs text-muted-foreground">{fmtMoney(b.subtotal_excl_vat)}</span>
    </span>
  );
};

export default function TilKontrollPage() {
  const isDesktop = useIsDesktop();
  const query = useQuery({ queryKey: ["invoicing", "review"], queryFn: async () => (await api.get("/invoicing/review-queue")).data });
  const items = query.data || [];
  return (
    <div className="space-y-5" data-testid="til-kontroll-page">
      <PageHeader title="Til kontroll" description="Ordrar som er melde utførte og ventar på kontroll og fakturagrunnlag." />
      {query.isLoading ? <LoadingList /> : null}
      {query.isError ? <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} /> : null}
      {query.isSuccess && items.length === 0 ? <EmptyState icon={ClipboardCheck} title="Ingenting til kontroll" description="Alle utførte ordrar har godkjent fakturagrunnlag." data-testid="til-kontroll-empty" /> : null}
      {query.isSuccess && items.length > 0 && isDesktop ? (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table data-testid="til-kontroll-list">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-10 text-xs">Ordre</TableHead>
                <TableHead className="h-10 text-xs">Kunde</TableHead>
                <TableHead className="h-10 text-xs">Meld utført</TableHead>
                <TableHead className="h-10 text-right text-xs">Timar</TableHead>
                <TableHead className="h-10 text-right text-xs">Varer</TableHead>
                <TableHead className="h-10 text-xs">Fakturagrunnlag</TableHead>
                <TableHead className="h-10 text-right text-xs">Handling</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((o) => (
                <TableRow key={o.id} data-testid="til-kontroll-row">
                  <TableCell className="py-2.5"><Link to={`/ordrar/${o.id}`} className="focus-ring block rounded-sm font-medium hover:underline"><span className="num block text-[11px] text-muted-foreground">{o.order_number}</span>{o.title}</Link></TableCell>
                  <TableCell>{o.customer_name}</TableCell>
                  <TableCell className="tabular whitespace-nowrap text-sm">{fmtDateTime(o.completed_at)}<span className="block text-xs text-muted-foreground">{o.completed_by_name}</span></TableCell>
                  <TableCell className="num text-right">{fmtMinutes((o.stats?.minutes || 0) + (o.stats?.resource_minutes || 0))}</TableCell>
                  <TableCell className="num text-right">{o.stats?.goods_count || 0}</TableCell>
                  <TableCell>{basisInfo(o)}</TableCell>
                  <TableCell className="text-right"><Button asChild size="sm" variant="outline" className="bg-card"><Link to={`/ordrar/${o.id}?tab=fakturagrunnlag`} data-testid="til-kontroll-open-button">Kontroller</Link></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
      {query.isSuccess && items.length > 0 && !isDesktop ? (
        <div className="grid gap-2" data-testid="til-kontroll-list">
          {items.map((o) => (
            <div key={o.id} className="rounded-lg border border-border bg-card p-4" data-testid="til-kontroll-card">
              <p className="num text-[11px] text-muted-foreground">{o.order_number}</p>
              <p className="text-[15px] font-semibold leading-snug">{o.title}</p>
              <p className="text-sm text-muted-foreground">{o.customer_name}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span>Utført <span className="tabular text-foreground">{fmtDateTime(o.completed_at)}</span></span>
                <span>Timar <span className="tabular text-foreground">{fmtMinutes((o.stats?.minutes || 0) + (o.stats?.resource_minutes || 0))}</span></span>
              </div>
              <div className="mt-3">{basisInfo(o)}</div>
              <Button asChild variant="outline" className="mt-3 h-11 w-full bg-card"><Link to={`/ordrar/${o.id}?tab=fakturagrunnlag`} data-testid="til-kontroll-open-button">Kontroller</Link></Button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
